<template>
    <div
        id="watershed-static-report"
        class="watershed static report"
    >
        <static-report
            :report-data="reportData"
            :sections="sections"
            @load="(sectionId) => $emit(`load-${sectionId}`)"
        />
    </div>
</template>


<script>
import StaticReport from '@foundryspatial/static-report';
import { LngLat } from 'mapbox-gl';

import AllocationsByIndustrySection from '@/components/WatershedReport/AllocationsByIndustrySection';
import AnnualHydrologySection from '@/components/WatershedReport/AnnualHydrologySection';
import ClimateSection from '@/components/WatershedReport/ClimateSection';
import HydrologicVariabilitySection from '@/components/WatershedReport/HydrologicVariabilitySection';
import IntroductionSection from '@/components/WatershedReport/IntroductionSection';
import LandcoverSection from '@/components/WatershedReport/LandcoverSection';
import MethodsSection from '@/components/WatershedReport/MethodsSection';
import MonthlyHydrologySection from '@/components/WatershedReport/MonthlyHydrologySection';
import NotesSection from '@/components/WatershedReport/NotesSection';
import OverviewSection from '@/components/WatershedReport/OverviewSection';
import ReferencesSection from '@/components/WatershedReport/ReferencesSection';
import ReportCover from '@/components/WatershedReport/ReportCover';
import PdfAllocationsTableSection from '@/components/WatershedReport/StaticReportAllocationsTableSection';
import TopographySection from '@/components/WatershedReport/TopographySection';

export default {
    name: 'WatershedStaticReport',

    components: {
        StaticReport,
    },

    data: () => ({
        // request parameters
        fwa: null,
        lngLat: null,
        watershedName: null,
        wfi: null,

        // data from API
        reportData: null,

        sections: [
            {
                title: 'Report Cover',
                id: 'report-cover',
                sectionComponent: ReportCover,
                classes: 'pdf-no-bottom-spacing',
            },
            {
                title: 'Introduction',
                id: 'introduction',
                sectionComponent: IntroductionSection,
            },
            {
                title: 'Overview',
                id: 'overview',
                sectionComponent: OverviewSection,
            },
            {
                title: 'Annual Hydrology',
                id: 'annual-hydrology',
                sectionComponent: AnnualHydrologySection,
                classes: 'pdf-no-bottom-spacing',
            },
            {
                title: 'Monthly Hydrology',
                id: 'monthly-hydrology',
                sectionComponent: MonthlyHydrologySection,
            },
            {
                title: 'Allocations By Industry',
                id: 'allocations-by-industry',
                sectionComponent: AllocationsByIndustrySection,
                classes: 'pdf-no-bottom-spacing',
            },
            {
                title: 'Allocations',
                id: 'allocations-table',
                sectionComponent: PdfAllocationsTableSection,
            },
            {
                title: 'Hydrologic Variability',
                id: 'hydrologic-variability',
                sectionComponent: HydrologicVariabilitySection,
                classes: 'pdf-no-bottom-spacing page-break-after',
            },
            {
                title: 'Landcover',
                id: 'landcover',
                sectionComponent: LandcoverSection,
                classes: 'page-break-after',
            },
            {
                title: 'Climate',
                id: 'climate',
                sectionComponent: ClimateSection,
                classes: 'pdf-no-bottom-spacing',
            },
            {
                title: 'Topography',
                id: 'topography',
                sectionComponent: TopographySection,
            },
            {
                title: 'Notes',
                id: 'notes',
                sectionComponent: NotesSection,
            },
            {
                title: 'References',
                id: 'references',
                sectionComponent: ReferencesSection,
            },
            {
                title: 'Methods',
                id: 'methods',
                sectionComponent: MethodsSection,
            },
        ],
    }),

    created() {
        // parse query paratmeters
        const {
            fwa,
            lat,
            lng,
            watershedName,
            wfi,
        } = this.$route.query;

        this.fwa = fwa;
        this.lngLat = new LngLat(lng, lat);
        this.watershedName = watershedName;
        this.wfi = wfi;

        // fetch data from API
        this.getData();
    },

    // define pageLoadPromise for the PDF converter service
    beforeCreate() {
        // promise to track data load/error
        const dataLoaded = new Promise((resolve, reject) => {
            this.$on('data-loaded', resolve);
            this.$on('data-error', reject);
        });

        // promises to track individual sections loading
        // (sections with asynchronous maps, etc.)
        const AnnualHydrologyLoaded = new Promise((resolve) => {
            this.$on('load-annual-hydrology', resolve);
        });
        const HydrologicVariabilityLoaded = new Promise((resolve) => {
            this.$on('load-hydrologic-variability', resolve);
        });
        const ReportCoverMapLoaded = new Promise((resolve) => {
            this.$on('load-report-cover', resolve);
        });

        // resolves when the page is ready for PDF snapshot
        window.pageLoadPromise = Promise.all([
            dataLoaded,
            AnnualHydrologyLoaded,
            HydrologicVariabilityLoaded,
            ReportCoverMapLoaded,
        ]);
    },

    methods: {
        /**
         * fetch all required report data from the API
         */
        async getData() {
            try {
                // load the watershed report information
                const apiResponse = await this.$api.getWatershedReport(
                    this.lngLat,
                    this.wfi,
                    this.watershedName,
                    this.fwa,
                );

                this.reportData = {
                    ...apiResponse,
                    // add click location to reportData
                    lngLat: this.lngLat,
                };

                // emit event to resolve dataLoaded promise
                this.$nextTick(() => {
                    this.$emit('data-loaded');
                });
            } catch (e) {
                // cause the dataLoaded promise to reject
                this.$emit('data-error');
                // re-throw to error handlers
                throw e;
            }
        },
    },
};
</script>

<style lang="scss">
#watershed-static-report {
    #summary.report-section {
        page-break-after: avoid;
        margin-bottom: 1em;
    }

    #allocations-table.report-section {
        // stylelint-disable-next-line no-descending-specificity
        td {
            .licence {
                width: 230px;
                white-space: normal;
            }

            .quantity {
                white-space: normal;
            }
        }
    }
}
</style>
